import { useMediaQuery, useTheme } from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { isValid } from "date-fns";
import React, { useEffect, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import { FormFieldWrapper } from "../FormFieldWrapper/FormFieldWrapper";
import aderantTheme from "../Theme/AderantTheme";
import { defaultDateToDisplay, getDate, isDateGreaterThan, isDateLessThan } from "../Utils/DateUtils";
import { mergeRefs } from "../Utils/Refs";
DatePicker.defaultProps = {
    hideValidationText: false,
    views: ["year", "month", "day"],
    disabled: false,
    hasError: false,
    required: false,
    labelProps: {
        width: "180px",
        position: "left"
    }
};
const paperStyles = ({ palette }) => ({
    "& .Mui-selected": {
        backgroundColor: palette.background.active,
        "&:hover": {
            backgroundColor: palette.background.active
        },
        "&:focus": {
            backgroundColor: palette.background.active
        }
    }
});
export function DatePicker(props) {
    const { "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, autofocus, disabled, label, onChange, onFocus, selectedDate, minDate, maxDate, onInternalError, views, dateFormat, locale } = props;
    const [errorProps, setErrorProps] = useState({ hasError: props.hasError ?? false, errorMessage: props.hasError ? props.errorMessage : "" });
    //popper state
    const [selection, setSelection] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const inputRef = React.useRef(null);
    const inputId = props.inputId ?? `${props.id ?? uuid()}-input`;
    const errorMessage = props.hasError ? props.errorMessage : "";
    useEffect(() => {
        setErrorProps(constructErrorProps(null));
    }, [props.hasError, errorMessage]);
    const constructErrorProps = (errorText) => {
        if (errorText) {
            return { hasError: true, errorMessage: errorText };
        }
        else if (props.hasError) {
            return { hasError: props.hasError, errorMessage: errorMessage };
        }
        return { hasError: false, errorMessage: "" };
    };
    //Monitor onChange for requiredness and error
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleOnChange = (value, context) => {
        const error = basicDateValidator(value);
        setErrorProps(constructErrorProps(error));
        if (onInternalError) {
            onInternalError(error);
        }
        if (onChange) {
            onChange(value);
        }
    };
    const handleOnFocus = (e) => {
        if (onFocus) {
            onFocus(e);
        }
    };
    /**
     * Basic date validator to ensure invlid date values do not go through
     * @param value Date Value
     * @returns void
     */
    const basicDateValidator = (value) => {
        const dateMin = getDate({ value: minDate, throwOnError: false, format: dateFormat });
        const dateMax = getDate({ value: maxDate, throwOnError: false, format: dateFormat });
        const dateEntered = getDate({ value: value, throwOnError: false, format: dateFormat });
        let isValidDate;
        if (!dateEntered) {
            return null;
        }
        //Validate entered date
        isValidDate = isValid(dateEntered);
        if (!isValidDate) {
            return `Entered date is invalid!`;
        }
        //Validate entered date respects minDate criteria
        if (dateMin && dateEntered) {
            isValidDate = isDateGreaterThan(dateEntered, dateMin);
            if (!isValidDate) {
                return `Date entered [${dateEntered.toLocaleDateString()}] should be greater than minDate [${dateMin.toLocaleDateString()}]`;
            }
        }
        //Validate entered date respects maxDate criteria
        if (dateMax && dateEntered) {
            isValidDate = isDateLessThan(dateEntered, dateMax, false);
            if (!isValidDate) {
                return `Date entered [${dateEntered.toLocaleDateString()}] should be less than maxDate [${dateMax.toLocaleDateString()}]`;
            }
        }
        return null;
    };
    const defaultInputRef = useRef(null);
    let allRefs = defaultInputRef;
    if (props.inputRef) {
        allRefs = mergeRefs(allRefs, props.inputRef);
    }
    return (React.createElement(FormFieldWrapper, { ...props, formFieldRef: inputRef, isMobile: isMobile, isValuePresent: !!props.selectedDate, inputId: inputId, id: props.id, ...errorProps, hideValidationText: props.hideValidationText },
        React.createElement("div", { style: { display: "flex", width: "100%", color: "pink" } },
            React.createElement(LocalizationProvider, { dateAdapter: AdapterDateFns, adapterLocale: locale },
                React.createElement(DesktopDatePicker, { disabled: disabled, format: dateFormat, slotProps: {
                        inputAdornment: { sx: { marginLeft: "0px" } },
                        desktopPaper: { sx: paperStyles },
                        popper: { placement: "bottom-start", style: { width: "auto" } },
                        textField: {
                            placeholder: undefined,
                            margin: "none",
                            inputRef: allRefs,
                            id: inputId,
                            sx: {
                                paddingLeft: !label || isMobile ? "1px" : "8px",
                                "& .MuiInput-root": {
                                    marginTop: 0
                                },
                                "& > p": {
                                    display: "none"
                                },
                                "& input::placeholder": {
                                    opacity: 0
                                },
                                "& input": {
                                    padding: "7px 4px"
                                },
                                ...(disabled && { backgroundColor: aderantTheme.palette.background.disabled }),
                                width: "100%"
                            },
                            variant: "standard",
                            "data-testid": "date-picker-desktop",
                            autoFocus: autofocus,
                            inputProps: {
                                "aria-label": ariaLabel,
                                "aria-labelledby": ariaLabelledBy,
                                onFocus: handleOnFocus
                            }
                        },
                        openPickerButton: {
                            "aria-label": "change date",
                            sx: {
                                ...(!isMobile && { borderRadius: "0px" }),
                                padding: "4px",
                                marginRight: "0px",
                                backgroundColor: selection ? "background.active" : "transparent",
                                color: selection ? "primary.contrastText" : "primary.main",
                                "@media (hover: none)": {
                                    ":hover": {
                                        backgroundColor: selection ? "background.active" : "transparent"
                                    }
                                },
                                "@media (hover: hover)": {
                                    ":hover": {
                                        backgroundColor: selection ? "background.active" : "background.hover"
                                    }
                                },
                                ...(disabled && { opacity: 0.4 })
                            }
                        }
                    }, value: typeof selectedDate === "string" ? new Date(selectedDate) : selectedDate, label: isMobile && label, onChange: handleOnChange, orientation: "portrait", onOpen: () => {
                        setSelection(true);
                    }, onClose: () => setSelection(false), closeOnSelect: true, minDate: minDate ?? undefined, maxDate: maxDate ?? undefined, defaultCalendarMonth: defaultDateToDisplay(minDate, maxDate), views: views })))));
}
